import React, { useState } from 'react'
import {
  Box,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  RadioGroup,
  Radio,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Rating,
  Button,
  Card,
  CardContent,
} from '@mui/material'
import {
  PhotoCamera as CameraIcon,
  Upload as UploadIcon,
} from '@mui/icons-material'
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { ChecklistCategory, Inspection } from '@types'
import ImageUploader from './ImageUploader'
import TreeSelect from './TreeSelect'

interface ChecklistInputProps {
  category: ChecklistCategory
  inspection?: Inspection | null
  value?: any
  onChange?: (value: any) => void
  disabled?: boolean
}

const ChecklistInput: React.FC<ChecklistInputProps> = ({
  category,
  inspection,
  value,
  onChange,
  disabled = false
}) => {
  const [inputValue, setInputValue] = useState(value || getDefaultValue())
  const [selectedItems, setSelectedItems] = useState<string[]>([])

  function getDefaultValue() {
    switch (category.input) {
      case 'boolean':
        return false
      case 'number':
        return 0
      case 'text':
        return ''
      case 'select':
      case 'options':
        return null
      case 'date':
        return null
      case 'list':
        return []
      default:
        return null
    }
  }

  const handleValueChange = (newValue: any) => {
    setInputValue(newValue)
    onChange?.(newValue)
  }

  const handleListItemToggle = (itemId: string) => {
    const newSelected = selectedItems.includes(itemId)
      ? selectedItems.filter(id => id !== itemId)
      : [...selectedItems, itemId]
    
    setSelectedItems(newSelected)
    handleValueChange(newSelected)
  }

  const renderInput = () => {
    switch (category.input) {
      case 'text':
        return (
          <TextField
            fullWidth
            multiline={category.type === 'textarea'}
            rows={category.type === 'textarea' ? 3 : 1}
            value={inputValue || ''}
            onChange={(e) => handleValueChange(e.target.value)}
            disabled={disabled}
            placeholder={`Enter ${category.name.toLowerCase()}...`}
          />
        )

      case 'number':
        return (
          <TextField
            fullWidth
            type="number"
            value={inputValue || ''}
            onChange={(e) => handleValueChange(parseFloat(e.target.value) || 0)}
            disabled={disabled}
            placeholder={`Enter ${category.name.toLowerCase()}...`}
          />
        )

      case 'boolean':
        return (
          <FormControlLabel
            control={
              <Checkbox
                checked={inputValue || false}
                onChange={(e) => handleValueChange(e.target.checked)}
                disabled={disabled}
              />
            }
            label={category.name}
          />
        )

      case 'select':
        return (
          <TreeSelect
            category={category}
            value={inputValue}
            onChange={handleValueChange}
            disabled={disabled}
          />
        )

      case 'options':
        return (
          <FormControl component="fieldset" fullWidth>
            <RadioGroup
              value={inputValue || ''}
              onChange={(e) => handleValueChange(parseInt(e.target.value))}
            >
              {category.options?.map((option: any) => (
                <FormControlLabel
                  key={option.id}
                  value={option.id}
                  control={<Radio disabled={disabled} />}
                  label={option.name}
                />
              ))}
            </RadioGroup>
          </FormControl>
        )

      case 'list':
        return (
          <Card variant="outlined">
            <List dense>
              {category.children.map((child: ChecklistCategory) => (
                <div key={child.id}>
                  <ListItem disablePadding>
                    <ListItemButton
                      onClick={() => handleListItemToggle(child.id.toString())}
                      disabled={disabled}
                    >
                      <ListItemIcon>
                        <Checkbox
                          checked={selectedItems.includes(child.id.toString())}
                          disabled={disabled}
                        />
                      </ListItemIcon>
                      <ListItemText primary={child.name} />
                    </ListItemButton>
                  </ListItem>

                  {/* Nested items */}
                  {child.children.map((nestedChild: ChecklistCategory) => (
                    <ListItem key={nestedChild.id} sx={{ pl: 4 }} disablePadding>
                      <ListItemButton
                        onClick={() => handleListItemToggle(nestedChild.id.toString())}
                        disabled={disabled}
                      >
                        <ListItemIcon>
                          <Checkbox
                            checked={selectedItems.includes(nestedChild.id.toString())}
                            disabled={disabled}
                          />
                        </ListItemIcon>
                        <ListItemText primary={nestedChild.name} />
                      </ListItemButton>
                    </ListItem>
                  ))}
                </div>
              ))}
            </List>
          </Card>
        )

      case 'date':
        return (
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DateTimePicker
              label={category.name}
              value={inputValue}
              onChange={handleValueChange}
              disabled={disabled}
              slotProps={{
                textField: {
                  fullWidth: true,
                },
              }}
            />
          </LocalizationProvider>
        )

      case 'image':
        return (
          <ImageUploader
            category={category}
            value={inputValue}
            onChange={handleValueChange}
            disabled={disabled}
          />
        )

      default:
        return (
          <TextField
            fullWidth
            value={inputValue || ''}
            onChange={(e) => handleValueChange(e.target.value)}
            disabled={disabled}
            placeholder={`Enter ${category.name.toLowerCase()}...`}
          />
        )
    }
  }

  return (
    <Box sx={{ mb: 3 }}>
      {category.input !== 'boolean' && category.input !== 'date' && (
        <Typography
          variant="subtitle2"
          component="label"
          sx={{
            display: 'block',
            mb: 1,
            fontWeight: 500,
            color: category.required ? 'error.main' : 'text.primary',
          }}
        >
          {category.name}
          {category.required && ' *'}
        </Typography>
      )}

      {renderInput()}

      {/* Description */}
      {category.type === 'description' && (
        <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
          {category.name}
        </Typography>
      )}

      {/* Icon indicator */}
      {category.icon && (
        <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
          <Typography variant="caption" color="textSecondary">
            Icon: {category.icon}
          </Typography>
        </Box>
      )}
    </Box>
  )
}

export default ChecklistInput
